import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const thisYear = () => new Date().getFullYear().toString();

export default function Holidays() {
  const { user } = useAuth();
  const canManage = ["partner","admin"].includes(user?.role);
  const [list, setList] = useState([]);
  const [year, setYear] = useState(thisYear());
  const [form, setForm] = useState({ date: "", name: "", description: "", kind: "public" });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/holidays?year=${year}`).then((r) => setList(r.data));
  useEffect(() => { load(); }, [year]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/holidays", form);
      toast.success("Holiday added");
      setForm({ date: "", name: "", description: "", kind: "public" });
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete holiday?")) return;
    try { await api.delete(`/holidays/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="holidays-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Public Holidays</div>
          <h1 className="font-sans text-4xl mt-2">Firm calendar</h1>
          <p className="text-slate-600 text-sm mt-1">Work logged on holidays auto-credits a Comp-Off.</p>
        </div>
        <div className="flex items-center gap-3">
          <Input value={year} onChange={(e)=>setYear(e.target.value)} className="rounded-sm w-28" data-testid="hol-year"/>
          {canManage && <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-holiday-btn"><Plus className="h-4 w-4 mr-2"/>Add Holiday</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-sans text-2xl">Add Holiday</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div><Label className="eyebrow">Date</Label><Input type="date" required value={form.date} onChange={(e)=>setForm({...form, date: e.target.value})} className="mt-2 rounded-sm" data-testid="hol-date"/></div>
                <div><Label className="eyebrow">Type</Label>
                  <select value={form.kind} onChange={(e)=>setForm({...form, kind: e.target.value, name: e.target.value === "saturday_off" && !form.name ? "Saturday off" : form.name})} className="mt-2 w-full h-10 border border-slate-200 rounded-sm px-3 text-sm" data-testid="hol-kind">
                    <option value="public">Public holiday</option><option value="saturday_off">Saturday off (one per month, declared by Partner)</option>
                  </select></div>
                <div><Label className="eyebrow">Name</Label><Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-2 rounded-sm" data-testid="hol-name"/></div>
                <div><Label className="eyebrow">Description (optional)</Label><Textarea rows={2} value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} className="mt-2 rounded-sm"/></div>
                <DialogFooter><Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="hol-save-btn">{busy ? "Saving..." : "Save"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase tracking-[0.18em] text-xs">
            <tr><th className="text-left p-4">Date</th><th className="text-left p-4">Day</th><th className="text-left p-4">Name</th><th className="text-left p-4">Description</th><th className="p-4"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="holidays-table">
            {list.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No holidays for {year}.</td></tr>}
            {list.map((h) => (
              <tr key={h.id} className="hover:bg-slate-50">
                <td className="p-4 font-mono text-xs">{h.date}</td>
                <td className="p-4 text-slate-600">{new Date(h.date).toLocaleDateString("en-IN", { weekday: "long" })}</td>
                <td className="p-4 font-medium">{h.name}{h.kind === "saturday_off" && <span className="ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-full bg-amber-50 text-amber-800 border-amber-200">Saturday off</span>}</td>
                <td className="p-4 text-slate-600">{h.description || "—"}</td>
                <td className="p-4 text-right">{canManage && <button onClick={()=>remove(h.id)} className="text-slate-400 hover:text-red-600" data-testid={`hol-delete-${h.id}`}><Trash2 className="h-4 w-4"/></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
